import { useQuery } from "@apollo/client/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Checkbox, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "~/components/ui";
import { GetProductsForInstallDocument, type GetProductsForInstallQuery } from "~/features/admin/tenants/gqls";
import {
  type BulkInstallResult,
  type BulkInstallTarget,
  useTenantBulkInstallProducts,
} from "~/features/admin/tenants/hooks";
import type { TenantRow } from "~/features/admin/tenants/types";

interface BulkInstallProductsDialogProps {
  open: boolean;
  onClose: () => void;
  /** The tenants selected in the list (targets of the bulk install). */
  tenants: TenantRow[];
  /** Called after a run finished and the dialog closed (refetch + clear selection). */
  onCompleted: () => void;
}

/**
 * Installs the checked products onto every selected tenant (connections start
 * as private). Already-installed and deactivated tenants are skipped up front;
 * the remaining (tenant, product) pairs run as sequential installs with
 * progress, and the result summarizes successes, skips, and failures.
 */
export function BulkInstallProductsDialog({ open, onClose, tenants, onCompleted }: BulkInstallProductsDialogProps) {
  const { t } = useTranslation();
  const { data } = useQuery<GetProductsForInstallQuery>(GetProductsForInstallDocument, { skip: !open });
  const products = useMemo(() => data?.products ?? [], [data]);

  const [checkedProductIds, setCheckedProductIds] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<(BulkInstallResult & { skipped: number }) | null>(null);
  const { run, running, progress } = useTenantBulkInstallProducts();

  const activeTenants = useMemo(() => tenants.filter((tenant) => !tenant.isDeactivated), [tenants]);
  const deactivatedCount = tenants.length - activeTenants.length;

  const plan = useMemo(() => {
    const targets: BulkInstallTarget[] = [];
    let skipped = 0;
    for (const product of products) {
      if (!checkedProductIds.has(product.id)) continue;
      for (const tenant of activeTenants) {
        if (tenant.installedProductIds.includes(product.id)) {
          skipped += 1;
          continue;
        }
        targets.push({
          tenantId: tenant.id,
          tenantName: tenant.name,
          productId: product.id,
          productName: product.name,
        });
      }
    }
    return { targets, skipped };
  }, [products, checkedProductIds, activeTenants]);

  const toggleProduct = (productId: string) => {
    setCheckedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleRun = async () => {
    const runResult = await run(plan.targets);
    setResult({ ...runResult, skipped: plan.skipped });
  };

  const handleOpenChange = (next: boolean) => {
    if (next || running) return;
    const finished = result !== null;
    setCheckedProductIds(new Set());
    setResult(null);
    onClose();
    if (finished) onCompleted();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="capitalize">{t("feature.tenants.bulkInstall.title")}</DialogTitle>
        </DialogHeader>

        {result === null ? (
          <div className="space-y-4">
            <p className="text-14-regular text-fg-2">
              {t("feature.tenants.bulkInstall.description", { count: tenants.length })}
            </p>

            <div className="space-y-2 rounded-md border border-border-default p-3">
              {products.map((product) => (
                <label
                  key={product.id}
                  htmlFor={`bulk-install-product-${product.id}`}
                  className="flex cursor-pointer items-center gap-2.5"
                >
                  <Checkbox
                    id={`bulk-install-product-${product.id}`}
                    checked={checkedProductIds.has(product.id)}
                    onCheckedChange={() => toggleProduct(product.id)}
                    disabled={running}
                  />
                  <span className="text-14-regular text-fg-1">{product.name}</span>
                </label>
              ))}
            </div>

            <div className="space-y-1 text-14-regular text-fg-2">
              <div>{t("feature.tenants.bulkInstall.planned", { count: plan.targets.length })}</div>
              {plan.skipped > 0 && (
                <div>{t("feature.tenants.bulkInstall.skippedInstalled", { count: plan.skipped })}</div>
              )}
              {deactivatedCount > 0 && (
                <div>{t("feature.tenants.bulkInstall.skippedDeactivated", { count: deactivatedCount })}</div>
              )}
            </div>

            {running && progress && (
              <div className="space-y-1.5">
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-subtle">
                  <div
                    className="h-full rounded-full bg-primary-500 transition-[width]"
                    style={{
                      width: `${progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100)}%`,
                    }}
                  />
                </div>
                <div className="text-12-regular tabular-nums text-fg-3">
                  {t("feature.tenants.bulkInstall.progress", { done: progress.done, total: progress.total })}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={running}>
                {t("ui.cancel")}
              </Button>
              <Button type="button" onClick={handleRun} disabled={running || plan.targets.length === 0}>
                {t("feature.tenants.bulkInstall.run", { count: plan.targets.length })}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1 text-14-regular text-fg-1">
              <div>{t("feature.tenants.bulkInstall.resultSucceeded", { count: result.succeeded })}</div>
              {result.skipped > 0 && (
                <div className="text-fg-2">
                  {t("feature.tenants.bulkInstall.skippedInstalled", { count: result.skipped })}
                </div>
              )}
              {result.failures.length > 0 && (
                <div className="text-danger">
                  {t("feature.tenants.bulkInstall.resultFailed", { count: result.failures.length })}
                </div>
              )}
            </div>

            {result.failures.length > 0 && (
              <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-border-default p-3 text-14-regular">
                {result.failures.map((failure) => (
                  <li key={`${failure.tenantId}:${failure.productId}`} className="text-fg-2">
                    <span className="font-medium text-fg-1">{failure.tenantName}</span>
                    <span className="mx-1 text-fg-3">/</span>
                    <span>{failure.productName}</span>
                    <span className="mx-1 text-fg-3">—</span>
                    <span className="text-danger">{failure.message}</span>
                  </li>
                ))}
              </ul>
            )}

            <DialogFooter>
              <Button type="button" onClick={() => handleOpenChange(false)}>
                {t("ui.close")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
